/*
 * @Author: Ryan
 * @Date: 2026-02-03
 * @Description: Why choose FalcoCut AI Subtitle Removal section with 6 feature cards
 */
import React from 'react';

interface FeatureCard {
  icon: string;
  title: string;
  description: string;
}

const features: FeatureCard[] = [
  {
    icon: '⚡️',
    title: 'Fast Processing',
    description:
      'Videos are processed in minutes, allowing you to clean clips without slowing down your schedule. The optimized AI keeps up with tight deadlines and multi‑project workloads.',
  },
  {
    icon: '🌐',
    title: 'Simple Editing for Everyone',
    description:
      'The interface is simple and intuitive, allowing anyone to clean up subtitles without a steep learning curve. Editors of all levels can produce polished videos in just a few steps.',
  },
  {
    icon: '✨',
    title: 'AI‑Powered Accuracy',
    description:
      'The AI handles subtitles, watermarks, and text with precision while preserving transitions, effects, and motion. You get pro‑level control without the complexity or extra plugins.',
  },
  {
    icon: '🔒',
    title: 'Secure Handling',
    description:
      'Enhanced safeguards protect your files throughout the editing process. Your content stays private and reliably processed with confidence with every project.',
  },
  {
    icon: '🧩',
    title: 'Seamless Across Devices',
    description:
      'Whether on desktop or mobile, the workflow stays smooth and consistent for quick subtitle cleanup. Your clips remain distraction‑free and ready to publish across platforms.',
  },
  {
    icon: '💰',
    title: 'Cost‑Effective Results',
    description:
      'Automated cleanup reduces the need for reshoots or manual editing, cutting both production time and labor costs. Use footage again without investing in extra tools or revisions.',
  },
];

const FeatureCardItem: React.FC<FeatureCard> = ({ icon, title, description }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 shadow-sm border border-gray-100">
      <div className="text-3xl pad:text-4xl mb-3 pad:mb-4">{icon}</div>
      <h3 className="text-lg pad:text-xl font-semibold text-gray-900 mb-2 pad:mb-3">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const WhyChooseSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Why Choose Our AI Subtitle Removal
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-4xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          FalcoCut gives you reliable AI subtitle cleanup that saves time, protects your
          content, and makes editing simple and secure. It&apos;s built to handle every
          project with consistency, so you can trust the results every time.
        </p>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 gap-4 pad:gap-6">
          {features.map((feature, index) => (
            <FeatureCardItem
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
